import { Directive, DirectiveBinding } from 'vue';
import { debounce, first, isObject, without } from 'lodash'

import { uuid } from '@wittignl/js-core'

import { popup } from '../mixin'
import TooltipPopup from '../component/popup/TooltipPopup.vue'
import type { OverlayPlugin } from '../plugin';

const bindings: Record<string, DirectiveBinding> = {};
const handlers = {};
const debouncedHandlers = {};
let openIds = [];

const ENTER_DEBOUNCE = 500;
const LEAVE_DEBOUNCE = 50;
const DEFAULT_PLACEMENT = 'top|bottom|left|right-center|start|end';

function showTooltip(el: HTMLElement) {

    const tooltipId = el.dataset.uiTooltipId;
    const binding = tooltipId ? bindings[tooltipId] : null;
    const component = binding?.instance
    const $overlay: OverlayPlugin = component['$overlay'];
    if(!$overlay){
        throw 'Overlay plugin not installed'
    }

    if(!binding || !tooltipId){
        return;
    }

    const value: any = binding.value;
    const content = isObject(value) ? (value as any).content : value;

    if(!content || openIds.includes(tooltipId)){
        return;
    }

    const modifiers = Object.keys(binding.modifiers).filter(mod => mod !== 'force');
    const placement = (isObject(value) ? (value as any).placement : null) || first(modifiers) || DEFAULT_PLACEMENT;

    // Close other tooltips
    for(const id of openIds){
        hideTooltip(id, $overlay, true);
    }

    openIds.push(tooltipId);

    if($overlay.isOpen(tooltipId)){
        return;
    }

    const showPopup = popup.bind(component);

    const tooltipOptions = {
        anchor: el,
        placement,
        pointerEvents: false
    };

    showPopup(TooltipPopup, tooltipOptions, { content }, {

        id: tooltipId,
        animate: true,
        blocking: false
    });
}

async function hideTooltip(tooltipId: string, $overlay, hard=false) {

    if(!$overlay.isOpen(tooltipId)){
        return;
    }

    // Disable animation
    if(hard){
        $overlay.updateOptions(tooltipId, { animate: false });
    }

    await $overlay.close(tooltipId);
    openIds = without(openIds, tooltipId);
}


export const tooltipDirective: Directive = {

    mounted: (el: HTMLElement, binding: DirectiveBinding) => {

        const vm = binding.instance;
        const $overlay: OverlayPlugin = vm['$overlay'];
        if(!$overlay){
            throw 'Overlay plugin not installed'
        }

        const tooltipId = `ui-tooltip-${uuid()}`;
        el.dataset.uiTooltipId = tooltipId;

        debouncedHandlers[tooltipId] = {
            enter: debounce(() => showTooltip(el), ENTER_DEBOUNCE),
            leave: debounce(() => hideTooltip(tooltipId, $overlay), LEAVE_DEBOUNCE)
        };

        const tooltipHandlers = {

            enter: () => {

                const dbHandlers = debouncedHandlers[tooltipId];

                dbHandlers.leave.cancel();
                dbHandlers.enter();
            },
            leave: () => {

                const dbHandlers = debouncedHandlers[tooltipId];

                dbHandlers.enter.cancel();
                dbHandlers.leave();
            },
            click: () => {

                const dbHandlers = debouncedHandlers[tooltipId];

                dbHandlers.leave.cancel();
                dbHandlers.enter.cancel();

                if(binding.modifiers.force && !$overlay.isOpen(tooltipId)){
                    showTooltip(el);
                }
                else {
                    hideTooltip(tooltipId, $overlay);
                }
            }
        };

        handlers[tooltipId] = tooltipHandlers;
        bindings[tooltipId] = binding;

        el.addEventListener('mouseenter', tooltipHandlers.enter);
        el.addEventListener('mouseleave', tooltipHandlers.leave);
        el.addEventListener('click', tooltipHandlers.click);
    },
    updated: (el: HTMLElement, binding: DirectiveBinding) => {

        const tooltipId = el.dataset.uiTooltipId;
        if(!tooltipId){
            return;
        }

        bindings[tooltipId] = binding;
    },
    unmounted: (el: HTMLElement, binding: DirectiveBinding) => {

        const tooltipId = el.dataset.uiTooltipId;
        if(!tooltipId){
            return;
        }

        const $overlay: OverlayPlugin = binding.instance['$overlay'];
        if(!$overlay){
            throw 'Overlay plugin not installed'
        }

        hideTooltip(tooltipId, $overlay, true);

        if(handlers[tooltipId]){

            el.removeEventListener('mouseenter', handlers[tooltipId].enter);
            el.removeEventListener('mouseleave', handlers[tooltipId].leave);
            el.removeEventListener('click', handlers[tooltipId].click);
        }

        delete handlers[tooltipId];
        delete bindings[tooltipId];
        delete debouncedHandlers[tooltipId];
    }
}

export default tooltipDirective;